import { useEffect, useState } from 'react';
import { Projects, Scenes } from 'trace-viewer/cms';
import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { apiConfig } from '../auth';

export interface NearbyState {
    projects: ProjectResponseDto[];
    independentScenes: SceneResponseDto[];
    loading: boolean;
    error: string | null;
}

export function useNearby(
    token: string | null,
    latitude: number | null,
    longitude: number | null,
): NearbyState {
    const [state, setState] = useState<NearbyState>({
        projects: [],
        independentScenes: [],
        loading: false,
        error: null,
    });

    useEffect(() => {
        if (!token || latitude == null || longitude == null) return;

        setState(s => ({ ...s, loading: true, error: null }));

        const config = apiConfig(token);
        const geoQuery = { latitude, longitude, distance: 5000 };

        Promise.all([
            new Projects(config).projectsList(geoQuery),
            new Scenes(config).scenesList(geoQuery),
        ])
            .then(([projectsRes, scenesRes]) => {
                const projects = projectsRes.data ?? [];
                const allScenes = scenesRes.data ?? [];
                // Independent scenes = those not attached to any project
                const independentScenes = allScenes.filter(s => !s.isAttached);

                setState({
                    projects,
                    independentScenes,
                    loading: false,
                    error: null,
                });
            })
            .catch(err => {
                setState(s => ({
                    ...s,
                    loading: false,
                    error: err.message ?? 'Failed to fetch nearby content',
                }));
            });
    }, [token, latitude, longitude]);

    return state;
}
